import { Box } from "@mui/material";
import { brainTokens } from "../../../design/brainTheme";
import type { PresenceSession } from "../../../lib/brain-api";
import { Icon } from "../../../mui";

const c = brainTokens.color;

function rel(ms: number): string {
  const h = Math.floor((Date.now() - ms) / 3600000);
  if (h < 1) return "just now";
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d} day${d === 1 ? "" : "s"} ago`;
}

export function RightNowPanel({ sessions }: { sessions: PresenceSession[] }) {
  const live = sessions.filter((s) => s.state === "active" || s.state === "held-unpushed");

  if (live.length === 0) {
    return (
      <Box
        sx={{
          border: `1px solid ${c.borderLight}`,
          borderRadius: `${brainTokens.radius.panel}px`,
          p: 2,
          background: c.panelBg2,
        }}
      >
        <Box
          sx={{
            fontFamily: brainTokens.font.mono,
            fontSize: 10.5,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: c.muted,
            mb: 1,
          }}
        >
          Right now
        </Box>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", color: c.muted2, fontSize: 13.5 }}>
          <Icon name="clock" size={16} />
          Idle — no active checkouts. A quiet desk is still a desk.
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
      <Box
        sx={{
          fontFamily: brainTokens.font.mono,
          fontSize: 10.5,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: c.muted,
        }}
      >
        Right now
      </Box>
      {live.map((s) => {
        const amber = s.state === "held-unpushed";
        const label = s.project || s.scope.replace(/^project:/, "");
        return (
          <Box
            key={`${s.scope}|${s.holder}`}
            sx={{
              border: `1px solid ${amber ? c.warnBorder : c.border}`,
              background: amber ? c.warnBg : c.pageBg,
              borderRadius: `${brainTokens.radius.card}px`,
              p: 1.75,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: amber ? c.warnText : c.successText,
                }}
              />
              <Box sx={{ fontSize: 14, fontWeight: 600, color: c.ink }}>
                {amber ? "Held with unpushed work" : "Active"}
              </Box>
            </Box>
            <Box sx={{ fontSize: 13.5, color: c.ink3 }}>
              {amber
                ? `${label} — checked out ${rel(s.startedAt)}, nothing pushed back yet`
                : `working on ${label} · checked out ${rel(s.startedAt)}`}
            </Box>
            {amber && (
              <Box sx={{ fontSize: 12.5, color: c.warnText, mt: 0.75 }}>
                Connect before continuing — the lease is advisory, but conflicts hurt.
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
}
